import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { styled } from '@mui/material/styles'

export interface FloatingActionToolbarProps {
  /** Left button (typically back/cancel action) */
  leftButton?: {
    label: string
    onClick: () => void
    variant?: 'contained' | 'outlined' | 'text'
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
    disabled?: boolean
    icon?: React.ReactNode
  }
  
  /** Right button (primary action) */
  rightButton?: {
    label: string
    onClick: () => void
    variant?: 'contained' | 'outlined' | 'text'
    color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'
    disabled?: boolean
    icon?: React.ReactNode
    size?: 'small' | 'medium' | 'large'
  }
  
  /** Whether to show enhanced animation when user reaches bottom */
  enhanceOnBottom?: boolean
  
  /** Additional styling props */
  sx?: any
}

// Styled Paper component for the floating toolbar
const StyledFloatingToolbar = styled(Paper)<{ isAtBottom?: boolean }>(({ theme, isAtBottom }) => ({
  position: 'fixed',
  bottom: 0, // Zero spacing at bottom
  left: theme.spacing(1.4), // 30% smaller: 2 * 0.7 = 1.4
  right: theme.spacing(1.4), // 30% smaller: 2 * 0.7 = 1.4
  zIndex: 1300, // Above most Material-UI components but below modals
  padding: theme.spacing(1.4), // 30% smaller: 2 * 0.7 = 1.4
  backgroundColor: 'rgba(255, 255, 255, 0.4)', // 60% transparent (40% opacity)
  backdropFilter: 'blur(1px)', // Reduced blur to 1px
  borderRadius: `${theme.spacing(2.1)} ${theme.spacing(2.1)} 0 0`, // 30% smaller: 3 * 0.7 = 2.1
  border: isAtBottom 
    ? `2px solid ${theme.palette.primary.dark}` // Dark blue border when at bottom
    : `1px solid ${theme.palette.grey[400]}`, // Light gray border normally
  borderBottom: 'none', // Remove bottom border to connect with screen edge
  boxShadow: isAtBottom 
    ? '0 8px 32px rgba(0, 0, 0, 0.15), 0 0 20px rgba(32, 138, 218, 0.2)' // Enhanced glow when at bottom
    : '0 4px 20px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)', // Modern smooth transition
  transform: isAtBottom ? 'scale(1.02)' : 'scale(1)', // Subtle scale effect when at bottom
  
  // Dark mode support
  ...(theme.palette.mode === 'dark' && {
    backgroundColor: 'rgba(18, 18, 18, 0.4)', // 60% transparent
    border: isAtBottom 
      ? `2px solid ${theme.palette.primary.light}` // Lighter blue for dark mode when at bottom
      : `1px solid ${theme.palette.grey[600]}`, // Dark gray border normally in dark mode
    borderBottom: 'none',
    boxShadow: isAtBottom
      ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 20px rgba(66, 196, 221, 0.3)'
      : '0 4px 20px rgba(0, 0, 0, 0.2)',
  }),
  
  // Mobile responsive adjustments
  [theme.breakpoints.down('sm')]: {
    bottom: 0, // Zero spacing on mobile too
    left: theme.spacing(0.7), // 30% smaller: 1 * 0.7 = 0.7
    right: theme.spacing(0.7), // 30% smaller: 1 * 0.7 = 0.7
    padding: theme.spacing(1.05), // 30% smaller: 1.5 * 0.7 = 1.05
    borderRadius: `${theme.spacing(1.75)} ${theme.spacing(1.75)} 0 0`, // 30% smaller: 2.5 * 0.7 = 1.75
    border: isAtBottom 
      ? `1.5px solid ${theme.palette.primary.dark}` // Thinner dark blue border on mobile when at bottom
      : `0.75px solid ${theme.palette.grey[400]}`, // Very thin light gray border normally on mobile
    borderBottom: 'none',
  },
}))

// Container for the toolbar content with max width
const ToolbarContainer = styled(Box)(({ theme }) => ({
  maxWidth: 1200,
  margin: '0 auto',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1.4), // 30% smaller: 2 * 0.7 = 1.4
  
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.7), // 30% smaller: 1 * 0.7 = 0.7
  },
}))

export const FloatingActionToolbar: React.FC<FloatingActionToolbarProps> = ({
  leftButton,
  rightButton,
  enhanceOnBottom = true,
  sx,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [isAtBottom, setIsAtBottom] = useState(false)

  useEffect(() => {
    if (!enhanceOnBottom) return

    const handleScroll = () => {
      // Check if user has scrolled to within 100px of the bottom
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const scrollHeight = document.documentElement.scrollHeight
      const clientHeight = window.innerHeight
      
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100
      setIsAtBottom(isNearBottom)
    }

    // Add scroll listener with throttling for better performance
    let ticking = false
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll()
          ticking = false
        })
        ticking = true
      }
    }

    window.addEventListener('scroll', throttledScroll, { passive: true })
    
    // Check initial state
    handleScroll()
    
    return () => window.removeEventListener('scroll', throttledScroll)
  }, [enhanceOnBottom])

  const renderButton = (
    buttonConfig: NonNullable<FloatingActionToolbarProps['leftButton']> | 
                  NonNullable<FloatingActionToolbarProps['rightButton']>,
    isRightButton = false
  ) => {
    const buttonSize = isRightButton && 'size' in buttonConfig ? buttonConfig.size : 'medium'
    
    return (
      <Button
        variant={buttonConfig.variant || 'contained'}
        color={buttonConfig.color || (isRightButton ? 'primary' : 'inherit')}
        size={buttonSize}
        onClick={buttonConfig.onClick}
        disabled={buttonConfig.disabled}
        startIcon={buttonConfig.icon}
        sx={{
          minWidth: isMobile ? 'auto' : 84, // 30% smaller: 120 * 0.7 = 84
          whiteSpace: 'nowrap',
          fontSize: isMobile ? '0.8125rem' : '0.875rem', // Slightly smaller font
          // Enhanced button styling
          ...(buttonConfig.variant === 'contained' && {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
              transform: 'translateY(-1px)',
            },
          }),
          // Make outlined buttons opaque (no transparency)
          ...(buttonConfig.variant === 'outlined' && {
            backgroundColor: theme.palette.background.paper, // Solid background
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }),
          // Mobile adjustments
          ...(isMobile && {
            fontSize: '0.8125rem', // 30% smaller font: 0.875 * 0.93 ≈ 0.8125
            padding: theme.spacing(0.7, 1.4), // 30% smaller: (1, 2) * 0.7 = (0.7, 1.4)
          }),
          // Desktop adjustments
          ...(!isMobile && {
            padding: theme.spacing(0.84, 1.68), // 30% smaller: (1.2, 2.4) * 0.7 = (0.84, 1.68)
          }),
        }}
      >
        {buttonConfig.label}
      </Button>
    )
  }

  return (
    <>
      {/* Spacer to prevent content overlap - account for toolbar height + padding (30% smaller) */}
      <Box sx={{ height: 56, flexShrink: 0 }} /> {/* 30% smaller: 80 * 0.7 = 56 */}
      
      {/* Floating Toolbar - Always visible with enhanced effects when at bottom */}
      <StyledFloatingToolbar elevation={8} isAtBottom={isAtBottom} sx={sx}>
        <ToolbarContainer>
          {/* Left Button */}
          <Box>
            {leftButton && renderButton(leftButton, false)}
          </Box>
          
          {/* Right Button */}
          <Box>
            {rightButton && renderButton(rightButton, true)}
          </Box>
        </ToolbarContainer>
      </StyledFloatingToolbar>
    </>
  )
}

export default FloatingActionToolbar